import { useState } from 'react';
import type { DashboardData } from '../../types/dashboard';
import { guidanceStateManager } from '../../utils/guidanceStateManager';
import type { OnboardingReason } from '../OnboardingDetector';

type SetDashboardData = React.Dispatch<React.SetStateAction<DashboardData>>;

export function useDashboardGuidance(setDashboardData: SetDashboardData) {
  const [, setGuidanceReason] = useState<OnboardingReason | null>(null);

  const handleGuidanceNeeded = (reason: OnboardingReason) => {
    setGuidanceReason(reason);

    setDashboardData((prev) => ({
      ...prev,
      guidance: {
        ...prev.guidance,
        enabled: true,
        currentStep: 0,
        dismissed: false,
      },
    }));

    guidanceStateManager.updateState({
      enabled: true,
      currentStep: 0,
      dismissed: false,
    });
  };

  const handleGuidanceNotNeeded = () => {
    setGuidanceReason(null);

    setDashboardData((prev) => ({
      ...prev,
      guidance: {
        ...prev.guidance,
        enabled: false,
      },
    }));
  };

  const handleGuidanceStepChange = (step: number) => {
    setDashboardData((prev) => ({
      ...prev,
      guidance: {
        ...prev.guidance,
        currentStep: step,
      },
    }));

    guidanceStateManager.updateState({
      currentStep: step,
    });
  };

  const handleGuidanceComplete = async () => {
    const newGuidanceState = {
      enabled: false,
      completedSteps: [0, 1, 2, 3],
      dismissed: false,
    };

    setDashboardData((prev) => ({
      ...prev,
      guidance: {
        ...prev.guidance,
        ...newGuidanceState,
        completionRate: 1,
      },
      user: {
        ...prev.user,
        guidanceCompleted: true,
      },
    }));

    guidanceStateManager.updateState({
      ...newGuidanceState,
      completedSteps: [0, 1, 2, 3],
    });
    guidanceStateManager.trackCompletion();

    setGuidanceReason(null);
  };

  const handleGuidanceDismiss = async () => {
    const newGuidanceState = {
      enabled: false,
      dismissed: true,
    };

    setDashboardData((prev) => ({
      ...prev,
      guidance: {
        ...prev.guidance,
        ...newGuidanceState,
      },
      user: {
        ...prev.user,
        guidanceCompleted: true,
      },
    }));

    guidanceStateManager.updateState(newGuidanceState);

    setGuidanceReason(null);
  };

  return {
    handleGuidanceNeeded,
    handleGuidanceNotNeeded,
    handleGuidanceStepChange,
    handleGuidanceComplete,
    handleGuidanceDismiss,
  };
}
